import { Component, OnInit, ElementRef, HostListener, inject } from '@angular/core';
import { Api } from '../../services/api';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AlertService } from '../../services/alert.service';
import { MdlManageAccount } from '../../components/mdl-manage-account/mdl-manage-account';
import { MdlDelete } from '../../components/mdl-delete/mdl-delete';
import { MdlChartInfo } from '../../components/mdl-chart-info/mdl-chart-info';
import { MdlFilterAccounts } from '../../components/mdl-filter-accounts/mdl-filter-accounts';

@Component({
  selector: 'app-accounts',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './accounts.html',
  styleUrls: ['./accounts.css']
})
export class Accounts implements OnInit {
  cuentas: any[] = [];
  cuentasFiltradas: any[] = [];
  isLoading: boolean = true;
  errorMessage: string = '';
  showRecentDropdown: boolean = false;
  selectedItem: string = 'todas';
  openedMenuId: string | null = null;
  searchTerm: string = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  activeFilters: any = null;

  readonly dialog = inject(MatDialog);

  constructor(
    private api: Api,
    private elementRef: ElementRef,
    private router: Router,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.cargarCuentas();
    this.initializeFilters();
  }


  private initializeFilters(): void {
    if (!this.activeFilters) {
      this.activeFilters = {
        allOwners: true,
        allTypes: true,
        creationDateRange: 'all',
        phoneFilter: 'all',
        owners: {},
        types: {}
      };
    }
  }

  openFilterModal(): void {
    const dialogRef = this.dialog.open(MdlFilterAccounts, {
      minWidth: '600px',
      maxHeight: '80vh',
      panelClass: 'mat-dlg-class',
      data: {
        cuentas: this.cuentas,
        filters: this.activeFilters
      }
    });

    dialogRef.afterClosed().subscribe((filters: any) => {
      if (filters) {
        this.activeFilters = filters;
        this.applyAdvancedFilters();
        this.alertService.success('Filtros aplicados', 'Filtros aplicados correctamente');
      }
    });
  }

  private applyAdvancedFilters(): void {
    let filteredAccounts = [...this.cuentas];

    if (!this.activeFilters.allOwners) {
      const selectedOwners = Object.keys(this.activeFilters.owners).filter(key => this.activeFilters.owners[key]);
      if (selectedOwners.length > 0) {
        filteredAccounts = filteredAccounts.filter(cuenta => {
          const ownerName = `${cuenta.ejecutivo?.nombre || ''} ${cuenta.ejecutivo?.apellido || ''}`.trim();
          return selectedOwners.includes(ownerName);
        });
      }
    }

    if (!this.activeFilters.allTypes) {
      const selectedTypes = Object.keys(this.activeFilters.types).filter(key => this.activeFilters.types[key]);
      if (selectedTypes.length > 0) {
        filteredAccounts = filteredAccounts.filter(cuenta => 
          selectedTypes.includes(cuenta.tipo_cuenta)
        );
      }
    }

    if (this.activeFilters.creationDateRange !== 'all') {
      filteredAccounts = this.filterByCreationDate(filteredAccounts, this.activeFilters.creationDateRange);
    }

    if (this.activeFilters.phoneFilter !== 'all') {
      filteredAccounts = this.filterByPhone(filteredAccounts, this.activeFilters.phoneFilter);
    }

    this.cuentasFiltradas = filteredAccounts;
    
    if (this.sortColumn) {
      this.applySorting();
    }
  }

  private filterByCreationDate(accounts: any[], dateRange: string): any[] {
    const hoy = new Date();
    
    switch (dateRange) {
      case 'today':
        return accounts.filter(cuenta => {
          if (!cuenta.fecha_creacion) return false;
          const fechaCreacion = new Date(cuenta.fecha_creacion);
          return fechaCreacion.toDateString() === hoy.toDateString();
        });
      
      case '7d':
        const sieteDiasAtras = new Date(hoy);
        sieteDiasAtras.setDate(hoy.getDate() - 7);
        return accounts.filter(cuenta => {
          if (!cuenta.fecha_creacion) return false;
          const fechaCreacion = new Date(cuenta.fecha_creacion);
          return fechaCreacion >= sieteDiasAtras && fechaCreacion <= hoy;
        });
      
      case '30d':
        const treintaDiasAtras = new Date(hoy);
        treintaDiasAtras.setDate(hoy.getDate() - 30);
        return accounts.filter(cuenta => {
          if (!cuenta.fecha_creacion) return false;
          const fechaCreacion = new Date(cuenta.fecha_creacion);
          return fechaCreacion >= treintaDiasAtras && fechaCreacion <= hoy;
        });
      
      default:
        return accounts;
    }
  }

  private filterByPhone(accounts: any[], phoneFilter: string): any[] {
    switch (phoneFilter) {
      case 'withPhone':
        return accounts.filter(cuenta => cuenta.telefono && cuenta.telefono.trim() !== '');
      case 'withoutPhone':
        return accounts.filter(cuenta => !cuenta.telefono || cuenta.telefono.trim() === '');
      default:
        return accounts;
    }
  }

  sortByColumn(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortColumn) return;

    this.cuentasFiltradas = [...this.cuentasFiltradas].sort((a, b) => {
      let valueA = this.getSortValue(a, this.sortColumn);
      let valueB = this.getSortValue(b, this.sortColumn);

      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  private getSortValue(cuenta: any, column: string): any {
    switch (column) {
      case 'nombre_empresa':
        return cuenta.nombre_empresa?.toLowerCase() || '';
      
      case 'telefono':
        return cuenta.telefono || '';
      
      case 'ejecutivo.nombre':
        return `${cuenta.ejecutivo?.nombre || ''} ${cuenta.ejecutivo?.apellido || ''}`.toLowerCase();
      
      default:
        return cuenta[column] || '';
    }
  }

  onSearchChange(): void {
    if (!this.searchTerm.trim()) {
      if (this.activeFilters) {
        this.applyAdvancedFilters();
      } else {
        this.cuentasFiltradas = [...this.cuentas];
      }
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.cuentasFiltradas = this.cuentas.filter(cuenta => 
        cuenta.nombre_empresa?.toLowerCase().includes(searchLower) ||
        cuenta.telefono?.includes(searchLower) ||
        cuenta.ejecutivo?.nombre?.toLowerCase().includes(searchLower) ||
        cuenta.ejecutivo?.apellido?.toLowerCase().includes(searchLower) ||
        cuenta.tipo_cuenta?.toLowerCase().includes(searchLower)
      );
    }
    
    if (this.sortColumn) {
      this.applySorting();
    }
  }

  hasActiveFilters(): boolean {
    if (!this.activeFilters) return false;
    
    const hasOwnerFilter = !this.activeFilters.allOwners && 
      Object.values(this.activeFilters.owners).some(val => val === true);
    const hasTypeFilter = !this.activeFilters.allTypes && 
      Object.values(this.activeFilters.types).some(val => val === true);
    const hasDateFilter = this.activeFilters.creationDateRange !== 'all';
    const hasPhoneFilter = this.activeFilters.phoneFilter !== 'all';
    
    return hasOwnerFilter || hasTypeFilter || hasDateFilter || hasPhoneFilter || this.searchTerm.trim() !== '';
  }

  clearFilters(): void {
    this.activeFilters = null;
    this.searchTerm = '';
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.initializeFilters();
    this.cuentasFiltradas = [...this.cuentas];
    this.alertService.success('Filtros limpiados', 'Los filtros han sido limpiados');
  }

  getDropdownText(): string {
    switch (this.selectedItem) {
      case 'todas': return 'Todas las cuentas';
      case 'recientes': return 'Cuentas recientemente mostradas';
      case 'ancladas': return 'Visto recientemente (lista anclada)';
      case 'mis': return 'Mis cuentas';
      default: return 'Vistos recientemente';
    }
  }


  cargarCuentas(): void {
    this.api.showLoading();
    this.isLoading = true;
    this.errorMessage = '';
    
    this.api.getAccounts().subscribe({
      next: (response: any) => {
        this.cuentas = (response.cuentas || response).map((cuenta: any) => {
          return {
            ...cuenta,
            id: cuenta.cuenta_id || cuenta.id 
          };
        });

        if (this.activeFilters) {
          this.applyAdvancedFilters();
        } else {
          this.cuentasFiltradas = [...this.cuentas];
        }
        setTimeout(() => {
          this.api.hideLoading();
          this.isLoading = false;
        }, 500);
      },
      error: (error) => {
        console.error('Error al cargar cuentas:', error);
        this.isLoading = false;
        this.errorMessage = 'Error al cargar la lista de cuentas';
        this.mostrarMensaje('Error al cargar cuentas', 'error');
        setTimeout(() => {
          this.api.hideLoading();
          this.isLoading = false;
        }, 500);
      }
    });
  }


  verDetalleCuenta(id: string, event?: MouseEvent): void {
    if (event) {
      const target = event.target as HTMLElement;
      const isCheckbox = target.tagName === 'INPUT' && target.getAttribute('type') === 'checkbox';
      const isDropdown = target.closest('.dropdown-container') !== null;
      
      if (isCheckbox || isDropdown) {
        return;
      }
    }

    if (!id || !this.esUUIDValido(id)) {
      this.mostrarMensaje('ID de cuenta no válido', 'error');
      return;
    }

    this.router.navigate(['account', id]).then(navigationResult => {
      if (!navigationResult) {
        this.mostrarMensaje('No se pudo cargar el detalle de la cuenta', 'error');
        console.error('Error de navegación a accounts/', id);
      }
    });
  }

  private esUUIDValido(id: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }

  mostrarMensaje(mensaje: string, tipo: 'exito' | 'error' | 'advertencia'): void {
    switch (tipo) {
      case 'exito':
        this.alertService.success('Éxito', mensaje);
        break;
      case 'error':
        this.alertService.error('Error', mensaje);
        break;
      case 'advertencia':
        this.alertService.warning('Advertencia', mensaje);
        break;
    }
  }

  toggleRecentDropdown(): void {
    this.showRecentDropdown = !this.showRecentDropdown;
  }

  selectItem(key: string): void {
    this.selectedItem = key;
    this.showRecentDropdown = false; 
  }

  toggleActionsMenu(id: string): void {
    this.openedMenuId = this.openedMenuId === id ? null : id;
    this.showRecentDropdown = false; 
  }

  openMdlCreateAccount(){
    this.dialog.open(MdlManageAccount,{
      minWidth: '800px',
      maxHeight: '900px',
      panelClass: 'mat-dlg-class',
      data:{
        mode: 'create'
      }
    });
  }

  updateAccount(data:any){
    this.dialog.open(MdlManageAccount,{
      minWidth: '800px',
      maxHeight: '900px',
      panelClass: 'mat-dlg-class',
      data:{
        mode: 'edit',
        account: data
      }
    });
  }

  openMdlDeleteAccount(id:string){
    this.dialog.open(MdlDelete, {
      minWidth: '500px',
      minHeight: '260px',
      panelClass: 'mat-dlg-class',
      data:{
        mode: 'account',
        id: id
      }
    });
  }

  openMdlCharts(){
    this.dialog.open(MdlChartInfo, {
      minWidth: '500px',
      minHeight: '260px',
      panelClass: 'mat-dlg-class',
      data: {
        mode: 'acc'
      }
    });
  }

  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: EventTarget | null): void {
    if (!(target instanceof HTMLElement)) return;
    
    const clickedInsideRecent = this.elementRef.nativeElement.querySelector('.dropdown-trigger')?.contains(target);
    const clickedInsideActions = this.elementRef.nativeElement.querySelector('.dots-button')?.contains(target);
    
    if (!clickedInsideRecent) {
      this.showRecentDropdown = false;
    }
    
    if (!clickedInsideActions) {
      this.openedMenuId = null;
    }
  }
}